import type { AuthUser } from '@/types/auth/authTypes';
import type { AreaPermissao, ChavePermissao } from '@/types/auth/permissoesTypes';

// Reexportados para quem já consome a util; o tipo mora em `src/types/auth/` (tipo de domínio cross-feature).
export type { AreaPermissao, ChavePermissao };

type UsuarioComPermissoes = Pick<AuthUser, 'permissoes'> | null | undefined;

/**
 * Sem `permissoes` a resposta é "não": a lista ausente (sessão antiga, payload sem o campo) não
 * pode virar acesso total. O `tipoUsuario` não entra — `ADMIN` já chega com o catálogo inteiro.
 */
function permissoesDo(user: UsuarioComPermissoes): readonly string[] {
  return user?.permissoes ?? [];
}

/**
 * A pessoa tem a chave.
 *
 * @example temPermissao(user, 'processos.reabrir')
 */
export function temPermissao(user: UsuarioComPermissoes, chave: ChavePermissao): boolean {
  return permissoesDo(user).includes(chave);
}

/**
 * A pessoa tem ao menos uma das chaves — para o ato que o core aceita por mais de uma porta.
 *
 * @example temAlgumaPermissao(user, ['processos.triar_lote', 'triagem.lote'])
 */
export function temAlgumaPermissao(
  user: UsuarioComPermissoes,
  chaves: readonly ChavePermissao[],
): boolean {
  const concedidas = permissoesDo(user);
  return chaves.some((chave) => concedidas.includes(chave));
}

/** Uma chave, ou a lista de chaves de que basta UMA (o core aceita o ato por mais de uma porta). */
export type ExigenciaPermissao = ChavePermissao | readonly ChavePermissao[];

/**
 * A exigência de um catálogo de ações (menu do processo, fila de Prazos) está atendida por `pode`.
 *
 * @example atendeExigencia(['processos.atribuir_arbitro', 'prazos.sortear_arbitro_fila'], pode)
 */
export function atendeExigencia(
  exigencia: ExigenciaPermissao,
  pode: (chave: ChavePermissao) => boolean,
): boolean {
  const chaves: readonly ChavePermissao[] = typeof exigencia === 'string' ? [exigencia] : exigencia;
  return chaves.some(pode);
}

/**
 * A pessoa tem qualquer chave de alguma das áreas. É a régua de LEITURA das telas da Secretaria
 * (decisão de produto): a tela aparece para quem tem ao menos uma ação nela.
 *
 * @example temPermissaoNaArea(user, 'triagem', 'prazos', 'processos')
 */
export function temPermissaoNaArea(
  user: UsuarioComPermissoes,
  ...areas: readonly AreaPermissao[]
): boolean {
  return permissoesDo(user).some((chave) => areas.some((area) => chave.startsWith(`${area}.`)));
}
